import React, { useState, useEffect } from 'react';
import { FileText, Image, MessageSquare, Plus, Edit, Trash2, Eye } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

export default function ContentManagement() {
  const [blogs, setBlogs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('blogs');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      
      // Fetch blogs
      const blogsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content/blogs`, {
        headers: { }
      });
      if (blogsRes.ok) {
        const blogsData = await blogsRes.json();
        setBlogs(blogsData.blogs || []);
      }

      // Fetch testimonials
      const testimonialsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content/testimonials`, {
        headers: { }
      });
      if (testimonialsRes.ok) {
        const testimonialsData = await testimonialsRes.json();
        setTestimonials(testimonialsData.testimonials || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlog = async (blogId) => {
    if (!confirm('Are you sure you want to delete this blog post?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content/blogs/${blogId}`, {
        method: 'DELETE',
        headers: { }
      });

      if (response.ok) {
        toast.success('Blog deleted successfully');
        fetchContent();
      }
    } catch (err) {
      toast.error('Failed to delete blog');
    }
  };

  const handleApproveTestimonial = async (testimonialId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/content/testimonials/${testimonialId}/approve`,
        {
          method: 'PATCH',
          headers: { }
        }
      );

      if (response.ok) {
        toast.success('Testimonial approved');
        fetchContent();
      }
    } catch (err) {
      toast.error('Failed to approve testimonial');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">⏳</div>
          <p className="text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Content Management</h1>
          <p className="text-gray-600 mt-1">Manage blogs, testimonials, and media</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Create New
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-2 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Blogs</p>
                <p className="text-2xl font-bold text-gray-900">{blogs.length}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Testimonials</p>
                <p className="text-2xl font-bold text-gray-900">{testimonials.length}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Review</p>
                <p className="text-2xl font-bold text-gray-900">
                  {testimonials.filter(t => t.status === 'pending').length}
                </p>
              </div>
              <MessageSquare className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="blogs">Blog Posts</TabsTrigger>
          <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
        </TabsList>

        {/* Blogs Tab */}
        <TabsContent value="blogs" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {blogs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No blog posts yet</p>
                  </div>
                ) : (
                  blogs.map((blog) => (
                    <div
                      key={blog._id}
                      className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex gap-4 flex-1">
                        {blog.image && (
                          <img
                            src={blog.image}
                            alt={blog.title}
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{blog.title}</h3>
                          <p className="text-sm text-gray-600 line-clamp-2">{blog.excerpt}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <Badge variant="outline">{blog.category || 'General'}</Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(blog.created_at).toLocaleDateString()}
                            </span>
                            <span className="text-xs text-gray-500">By {blog.author}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="text-blue-600">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-green-600">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => handleDeleteBlog(blog._id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Testimonials Tab */}
        <TabsContent value="testimonials" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {testimonials.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No testimonials yet</p>
                  </div>
                ) : (
                  testimonials.map((testimonial) => (
                    <div
                      key={testimonial._id}
                      className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {testimonial.patient_name?.[0] || 'P'}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{testimonial.patient_name}</h4>
                            <p className="text-sm text-gray-600 mt-1">{testimonial.content}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <span
                                    key={i}
                                    className={`text-lg ${
                                      i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'
                                    }`}
                                  >
                                    ★
                                  </span>
                                ))}
                              </div>
                              <Badge
                                className={
                                  testimonial.status === 'approved'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }
                              >
                                {testimonial.status || 'pending'}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {new Date(testimonial.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        {testimonial.status === 'pending' && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleApproveTestimonial(testimonial._id)}
                          >
                            Approve
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
